import { BehaviorSubject } from 'rxjs';
import { localDB } from '../../db/client';
import { realtimeService } from '../../api/signalr';
import { serverSyncService } from './server-sync-service'
import { logger } from '../utils/logger'
import { setSentryUser, clearSentryUser } from '../utils/sentry'
import { memoryStateService } from '../state/memory-store'
interface User {
  userId: string;
  userDetails: string;
  identityProvider: string;
  userRoles: string[];
}

class UserContextService {
  private userSubject = new BehaviorSubject<User | null>(null);
  public user$ = this.userSubject.asObservable();
  private currentUserId: string | null = null;
  private initInFlight: Promise<void> | null = null;

  async init() {
    if (this.initInFlight) {
      return this.initInFlight;
    }

    this.initInFlight = this.initInternal();
    try {
      await this.initInFlight;
    } finally {
      this.initInFlight = null;
    }
  }

  private async initInternal() {
    // First, check cache synchronously to enable offline-first experience
    // Optimization: If we already in-memory, assume cache was already loaded
    // This prevents redundant cache reads and logging on navigation
    if (this.userSubject.value) {
      // Just check server session validity below
      // We pass userFromCache=null so verification logic compares against current state if needed
      // (Actually validation logic compares against userFromCache, so we need to be careful)
    } 
    
    // We only load from cache if we don't have a user
    const loadFromCache = !this.userSubject.value;
    const cachedUser = loadFromCache ? localStorage.getItem('auth_user') : null;
    let userFromCache: User | null = null;
    
    if (cachedUser) {
      try {
        const parsedUser = JSON.parse(cachedUser);
        console.info('[UserContext] Restoring session from cache for:', parsedUser.userDetails);
        // Setup user synchronously from cache (this enables immediate UI render)
        await this.setupUser(parsedUser);
        userFromCache = parsedUser;

      } catch (e) {
        console.error('[UserContext] Failed to restore cached auth:', e);
        localStorage.removeItem('auth_user');
      }
    } else if (!loadFromCache) {
      // If we skipped loading from cache, use the current user as "cached" reference
      // so we don't unnecessarily call setupUser if server matches
      userFromCache = this.userSubject.value; 
    }

    // Then, verify with server in background (non-blocking)
    try {
      const response = await fetch('/.auth/me');
      if (response.ok) {
        const data = await response.json();
        if (data.clientPrincipal) {
          const serverUser = data.clientPrincipal;
          
          // Only re-setup if user changed or if we didn't have a cached user
          if (!userFromCache || serverUser.userId !== userFromCache.userId) {
            await this.setupUser(serverUser);
          }
        } else {
          // Server explicitly returns null principal - session invalid
          // Clear any cached user
          if (userFromCache) {
            console.info('[UserContext] Server session expired, clearing cache');
            this.clearUser();
          }
          return;
        }
      }
    } catch (error) {
      console.warn('[UserContext] Auth check failed (network error), using cached session:', error);
      // If we already restored from cache, we're good to continue offline
      // If not, clear user
      if (!userFromCache) {
        this.clearUser();
      }
    }
  }

  private async setupUser(user: User) {
    const currentUserId = this.userSubject.value?.userId;
    if (user.userId !== currentUserId) {
      if (currentUserId) {
        logger.info('[UserContext] Switching users, cleaning up previous session...')
        // Cleanup previous user state
        memoryStateService.clear()
        serverSyncService?.cleanup?.()
        serverSyncService?.reset?.()
        realtimeService?.cleanup?.()
      }

      // Clear stale user_details so getUserDetails() won't return the
      // previous user's profile from cache before the network call completes.
      // This is defense-in-depth: handleSwitchAccount / logout should also
      // clear these, but if they didn't (e.g. crash, direct URL navigation),
      // this prevents cross-user data leakage.
      localStorage.removeItem('user_details')

      // Initialize DB for new user (closes old one automatically)
      await localDB.init(user.userId);
      localStorage.setItem('auth_user', JSON.stringify(user));
      this.setCurrentUserId(user.userId);
      this.userSubject.next(user);

      // Set Sentry user context for error tracking
      setSentryUser(user.userId, user.userDetails)

      // Wire up message handler for realtime events
      realtimeService.setMessageReceivedHandler(async (message: any) => {
        await serverSyncService.handleMessageReceived(message)
      })

      // Start user-scoped background sync only after LocalDB is initialized
      // ServerSyncService will wire up realtime lifecycle event listeners
      await serverSyncService.initialize()

      realtimeService.initialize().catch((err: Error) => {
        logger.error('[UserContextService] Failed to start realtime service:', err);
      });

      // Eagerly start user group sync via HTTP (don't wait for SignalR connection).
      // On a new device, waiting for SignalR negotiate + connect + group join can
      // take many seconds.  Fetching scenario headers immediately lets the homepage
      // populate faster.  The deduplication guard in handleUserGroupConnected
      // ensures no double work when SignalR later fires onUserGroupJoined.
      serverSyncService.handleUserGroupConnected().catch((err: Error) => {
        logger.warn('[UserContext] Eager scenario sync failed (will retry on SignalR connect):', err);
      });
    }
  }

  private clearUser() {
    logger.info('[UserContext] Clearing user session...')
    localStorage.removeItem('auth_user');
    localStorage.removeItem('user_details');
    
    // Clear application state
    memoryStateService.clear()
    serverSyncService?.cleanup?.()
    serverSyncService?.reset?.()
    realtimeService?.cleanup?.()

    this.userSubject.next(null);
    this.currentUserId = null;

    // Clear Sentry user context
    clearSentryUser()
  }

  public getCurrentUser(): User | null {
    return this.userSubject.value;
  }

  public onAuthStateChanged(callback: (user: User | null) => void) {
    return this.user$.subscribe(callback);
  }

  public setCurrentUserId(userId: string | null): void {
    this.currentUserId = userId;
    logger.debug('UserContext: Current user set to', userId);
  }

  public getCurrentUserIdOrThrow(): string {
    if (!this.currentUserId) {
      throw new Error('User not authenticated');
    }
    return this.currentUserId;
  }


  async getUserDetails() {
    const userId = this.getCurrentUserIdOrThrow();
    // Try cache first
    const cachedUserDetails = localStorage.getItem('user_details');
    if (cachedUserDetails) {
      try {
        const parsed = JSON.parse(cachedUserDetails);
        if (parsed?.id === userId) {
          return parsed;
        }
      } catch (e) {
        localStorage.removeItem('user_details');
      }
    }

    // Post to get user profile (server will update login time).
    // Add a timeout so the UI can surface a retry instead of hanging forever.
    const controller = new AbortController();
    const timeoutMs = 15000;
    const timeoutHandle = window.setTimeout(() => controller.abort(), timeoutMs);

    let details: any;
    try {
      const response = await fetch('/api/auth/user', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({}),
        signal: controller.signal
      });

      if (!response.ok) {
        let errorBody = '';
        try {
          errorBody = await response.text();
        } catch {
          // ignore
        }

        const singleLine = (errorBody || '').replace(/\s+/g, ' ').trim();
        const suffix = singleLine ? ` - ${singleLine}` : '';
        throw new Error(`Failed to load user profile: ${response.status} ${response.statusText}${suffix}`);
      }

      details = await response.json();
    } catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') {
        throw new Error('Failed to load user profile: request timed out');
      }
      throw e;
    } finally {
      window.clearTimeout(timeoutHandle);
    }

    localStorage.setItem('user_details', JSON.stringify(details));
    return details;
  }
}

export const userContextService = new UserContextService();
